<template>
  <div class="att-logs">

    <!-- Header -->
    <div class="logs-header">
      <div class="logs-header-left">
        <div class="logs-header-icon"><i class="bi bi-journal-text"></i></div>
        <div>
          <h5 class="logs-title mb-0">Attendance Logs</h5>
          <small class="logs-sub">Raw biometric punch records</small>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="logs-filters">
      <div class="filter-group">
        <label>Date</label>
        <input v-model="filters.date" type="date" class="form-control form-control-sm" @change="load" />
      </div>
      <div class="filter-group filter-group-search">
        <label>Search</label>
        <div class="search-wrap">
          <i class="bi bi-search search-icon"></i>
          <input v-model="filters.search" type="search" class="form-control form-control-sm search-input"
                 placeholder="Name or ID…" @keyup.enter="load" />
        </div>
      </div>
      <div class="filter-group">
        <label>Status</label>
        <select v-model="filters.status" class="form-control form-control-sm" @change="load">
          <option value="">All</option>
          <option value="IN">IN</option>
          <option value="OUT">OUT</option>
        </select>
      </div>
      <div class="filter-group filter-group-btn">
        <label>&nbsp;</label>
        <button class="btn btn-primary btn-sm" @click="load">
          <i class="bi bi-search mr-1"></i> Search
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="logs-state">
      <div class="spinner-border spinner-border-sm text-primary mr-2"></div> Loading…
    </div>

    <!-- Table -->
    <div v-else class="logs-table-wrap">
      <table class="logs-table">
        <thead>
          <tr>
            <th>Personnel ID</th>
            <th>Name</th>
            <th>Date</th>
            <th>Time</th>
            <th>Status</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td colspan="6" class="logs-empty">No records found.</td>
          </tr>
          <tr v-for="row in rows" :key="row.id">
            <td class="col-id">{{ row.personnelID }}</td>
            <td class="col-name">{{ row.lastName }}, {{ row.firstName }}</td>
            <td class="col-date">{{ row.date }}</td>
            <td class="col-time">{{ formatTime(row.time) }}</td>
            <td class="col-status">
              <span class="status-badge" :class="row.inOutStatus === 'IN' ? 'badge-in' : 'badge-out'">
                <i :class="row.inOutStatus === 'IN' ? 'bi bi-box-arrow-in-right' : 'bi bi-box-arrow-right'"></i>
                {{ row.inOutStatus }}
              </span>
            </td>
            <td class="col-source">
              <span class="source-badge" :class="sourceBadgeClass(row.source)">
                {{ sourceLabel(row.source) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="meta.last_page > 1" class="logs-pagination">
        <button class="page-btn" :disabled="meta.current_page === 1" @click="changePage(meta.current_page - 1)">
          <i class="bi bi-chevron-left"></i>
        </button>
        <span class="page-info">Page {{ meta.current_page }} of {{ meta.last_page }} &nbsp;·&nbsp; {{ meta.total }} records</span>
        <button class="page-btn" :disabled="meta.current_page === meta.last_page" @click="changePage(meta.current_page + 1)">
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>
      <div v-else-if="meta.total" class="logs-count">{{ meta.total }} record{{ meta.total !== 1 ? 's' : '' }}</div>
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AttendanceLogs",
  data() {
    return {
      loading: false,
      rows: [],
      meta: { current_page: 1, last_page: 1, total: 0 },
      filters: {
        date: new Date().toISOString().slice(0, 10),
        search: "",
        status: "",
      },
      page: 1,
    };
  },
  methods: {
    async load() {
      this.page = 1;
      await this.fetch();
    },
    async fetch() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: 100, ...this.filters };
        const { data } = await axios.get("api/attendance/logs", { params });
        this.rows = data.data;
        this.meta = {
          current_page: data.current_page,
          last_page:    data.last_page,
          total:        data.total,
        };
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    changePage(p) {
      this.page = p;
      this.fetch();
    },
    formatTime(t) {
      if (!t) return "—";
      const [h, m] = t.split(":");
      const hr = parseInt(h);
      return `${hr % 12 || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`;
    },
    sourceLabel(source) {
      const s = source || "biometric";
      if (s === "web" || s === "pass_slip") return "System";
      return "Biometric";
    },
    sourceBadgeClass(source) {
      const s = source || "biometric";
      return s === "web" || s === "pass_slip" ? "source-web" : "source-bio";
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.att-logs {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 4px;
}

/* Header */
.logs-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}
.logs-header-left { display: flex; align-items: center; gap: 12px; }
.logs-header-icon {
  width: 42px; height: 42px; border-radius: 12px;
  background: #e8f0fe; color: #0d6efd;
  display: flex; align-items: center; justify-content: center; font-size: 1.2rem;
}
.logs-title { font-size: 1.1rem; font-weight: 700; color: #1a2340; }
.logs-sub   { font-size: 0.8rem; color: #8a94a6; }

/* Filters */
.logs-filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  background: #fff;
  border: 1.5px solid #e9ecef;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
}
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-group label { font-size: 0.72rem; font-weight: 600; color: #6c757d; text-transform: uppercase; letter-spacing: 0.03em; }
.filter-group-search { flex: 1; min-width: 180px; }
.filter-group-btn label { visibility: hidden; }

.search-wrap { position: relative; }
.search-icon { position: absolute; left: 8px; top: 50%; transform: translateY(-50%); color: #adb5bd; font-size: 0.8rem; pointer-events: none; }
.search-input { padding-left: 28px !important; }

/* Loading / empty */
.logs-state { text-align: center; padding: 40px; color: #6c757d; display: flex; align-items: center; justify-content: center; gap: 8px; }

/* Table */
.logs-table-wrap {
  background: #fff;
  border: 1.5px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}
.logs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.83rem;
}
.logs-table thead th {
  background: #f8f9fb;
  padding: 10px 14px;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  border-bottom: 1.5px solid #e9ecef;
}
.logs-table tbody tr {
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.1s;
}
.logs-table tbody tr:last-child { border-bottom: none; }
.logs-table tbody tr:hover { background: #f8f9fb; }
.logs-table td { padding: 9px 14px; color: #1a2340; vertical-align: middle; }

.col-id   { font-family: monospace; color: #6c757d; width: 120px; }
.col-name { font-weight: 500; }
.col-date { color: #6c757d; width: 110px; }
.col-time { font-weight: 600; width: 90px; }
.col-status { width: 90px; }
.col-source { width: 100px; }

.status-badge {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.72rem; font-weight: 700; padding: 3px 8px; border-radius: 99px;
}
.badge-in  { background: #d1e7dd; color: #155724; }
.badge-out { background: #cfe2ff; color: #084298; }

.source-badge {
  display: inline-flex; align-items: center;
  font-size: 0.7rem; font-weight: 700; padding: 3px 8px; border-radius: 99px;
}
.source-web { background: #e8f0fe; color: #3a6fd4; }
.source-bio { background: #f1f3f5; color: #6c757d; }

.logs-empty { text-align: center; padding: 32px; color: #adb5bd; }

/* Pagination */
.logs-pagination {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 12px 16px; border-top: 1px solid #f1f3f5;
}
.page-btn {
  width: 30px; height: 30px; border-radius: 6px; border: 1.5px solid #dee2e6;
  background: #fff; cursor: pointer; color: #6c757d;
  display: flex; align-items: center; justify-content: center;
}
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-btn:not(:disabled):hover { border-color: #0d6efd; color: #0d6efd; }
.page-info { font-size: 0.8rem; color: #6c757d; }
.logs-count { text-align: right; padding: 10px 16px; font-size: 0.78rem; color: #adb5bd; border-top: 1px solid #f1f3f5; }
</style>
